import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert } from 'antd';
import { routes } from '../../../../utils/helpers/ApiClient.jsx';
import { ContentBlock, Spinner } from '../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import { useGetChangeRequestApprovalByTokenQuery } from '../../../../redux/services/ing_projects_change_requests.jsx';
import ProtectedEmailLink from '../../../common/ProtectedEmailLink/ProtectedEmailLink.jsx';

const mapAkciaToAction = (akcia) => {
  const norm = (akcia || '').toLowerCase();
  if (norm === 'schvalit') return 'approve';
  if (norm === 'zamietnut') return 'reject';
  return null;
};

const RequestDetails = ({ data, t }) => (
  <>
    <br />
    <br />
    <strong>{t('Študent')}</strong>: {data.student_name}
    <br />
    <strong>{t('Pôvodný projekt')}</strong>: {data.original_project}
    <br />
    <strong>{t('Nový projekt')}</strong>: {data.new_project}
    <br />
    <strong>{t('Študijný program')}</strong>: {data.study_program}
  </>
);

const resolveStateKey = (state) => {
  if (state === null || state === undefined) {
    return 'pending';
  }

  if (typeof state === 'string') {
    const trimmedState = state.trim();
    if (trimmedState === '' || trimmedState === 'Čaká na schválenie' || trimmedState === '0') {
      return 'pending';
    }
    if (trimmedState === 'Schválená' || trimmedState === '1') {
      return 'approved';
    }
    if (trimmedState === 'Zamietnutá' || trimmedState === '2') {
      return 'rejected';
    }
  }

  if (typeof state === 'number') {
    if (state === 0) {
      return 'pending';
    }
    if (state === 1) {
      return 'approved';
    }
    if (state === 2) {
      return 'rejected';
    }
  }

  return 'pending';
};

const getAlreadyProcessedAlert = (error, t) => {
  const reason = error?.data?.reason;
  const details = error?.data?.data;

  if (reason === 'already_approved_by_you') {
    return {
      type: 'info',
      title: t('Žiadosť už bola spracovaná'),
      description: (
        <>
          {t('Túto žiadosť ste už za seba schválili.')}
          {resolveStateKey(details?.state) === 'pending' && (
            <>
              <br />
              {t('Žiadosť je zatiaľ len čiastočne schválená. Čaká sa na vyjadrenie ostatných schvaľovateľov.')}
            </>
          )}
          {details && <RequestDetails data={details} t={t} />}
        </>
      ),
    };
  }

  if (reason === 'already_rejected_by_you') {
    return {
      type: 'info',
      title: t('Žiadosť už bola spracovaná'),
      description: (
        <>
          {t('Túto žiadosť ste už za seba zamietli.')}
          {details && <RequestDetails data={details} t={t} />}
        </>
      ),
    };
  }

  if (reason === 'request_already_approved') {
    return {
      type: 'success',
      message: t('Žiadosť už bola schválená'),
      description: (
        <>
          {t('Žiadosť už bola schválená všetkými garantmi.')}
          {details && <RequestDetails data={details} t={t} />}
        </>
      ),
    };
  }

  if (reason === 'request_already_rejected') {
    return {
      type: 'warning',
      message: t('Žiadosť bola zamietnutá.'),
      description: (
        <>
          {t('Žiadosť bola zamietnutá iným schvaľovateľom.')}
          {details && <RequestDetails data={details} t={t} />}
        </>
      ),
    };
  }

  return null;
};

const IpChangeRequestApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const akcia = searchParams.get('akcia');
  const token = searchParams.get('token');

  const { t } = useTranslation();

  const action = mapAkciaToAction(akcia);

  const { data, error, isError, isSuccess, isLoading } =
    useGetChangeRequestApprovalByTokenQuery(
      { action, token },
      { skip: !action || !token },
    );

  const pageTitle = t('Spracovanie žiadosti o zmenu projektu');

  useEffect(() => {
    document.title = pageTitle + ' | FRI UNIZA';
  }, [pageTitle]);

  const invalidParams = !action || !token;
  const alreadyProcessed = isError ? getAlreadyProcessedAlert(error, t) : null;
  const successStateKey = resolveStateKey(data?.data?.state);
  const isApproveAction = action === 'approve';
  const isPartiallyApproved = isApproveAction && successStateKey === 'pending';
  const successAlertStatus = isApproveAction
    ? (isPartiallyApproved ? 'info' : 'success')
    : 'warning';

  return isLoading ? (
    <Spinner />
  ) : (
    <div className="news-detail-page-container">
      <ContentBlock
        contentVisible
        shadow={true}
        title={pageTitle}
        backTo={routes.homePage}
      >
        <div style={{ maxWidth: 800, margin: '10px auto', marginBottom: 10 }}>
          <p>
            {t(
              'Ďakujeme, že ste si našli čas na spracovanie žiadosti o zmenu inžinierskeho projektu. Nižšie sa nachádzajú informácie o výsledku spracovania:',
            )}
          </p>

          {invalidParams && (
            <Alert
              type="warning"
              showIcon
              title={t('Žiadosť nie je možné spracovať')}
              description={t(
                'Parametre žiadosti chýbajú alebo sú v nesprávnom tvare. Prihláste sa do portálu FRI a žiadosť spracujte v module Inžinierske projekty.',
              )}
              style={{ marginTop: 12 }}
            />
          )}

          {!invalidParams && isError && alreadyProcessed && (
            <Alert
              type={alreadyProcessed.type}
              showIcon
              title={alreadyProcessed.title}
              description={alreadyProcessed.description}
              style={{ marginTop: 12, marginBottom: 12 }}
            />
          )}

          {!invalidParams && isError && !alreadyProcessed && (
            <Alert
              type="warning"
              showIcon
              title={t('Žiadosť nie je možné spracovať')}
              description={
                error?.data?.message ||
                t('Nastala chyba pri spracovaní žiadosti.')
              }
              style={{ marginTop: 12, marginBottom: 12 }}
            />
          )}

          {!invalidParams && isSuccess && (
            <Alert
              type={successAlertStatus}
              showIcon
              title={
                isPartiallyApproved
                  ? t('Vaše schválenie bolo zaznamenané')
                  : data?.message || t('Žiadosť bola úspešne spracovaná.')
              }
              description={
                <>
                  {isPartiallyApproved ? (
                    <>
                      {t('Žiadosť je zatiaľ len')}{' '}
                      <strong>{t('čiastočne schválená')}</strong>
                      {' '}
                      {t('a na finálne rozhodnutie je potrebné vyjadrenie zvyšných schvaľovateľov.')}
                    </>
                  ) : (
                    <>
                      {t('Žiadosť o zmenu projektu bola')}{' '}
                      <strong>
                        {action === 'approve'
                          ? t('schválená')
                          : t('zamietnutá')}
                      </strong>
                      {'. '}
                    </>
                  )}
                  {data?.data && <RequestDetails data={data.data} t={t} />}
                </>
              }
              style={{ marginTop: 12, marginBottom: 12 }}
            />
          )}

          <p style={{ marginTop: 12, paddingBottom: 20 }}>
            {t(
              'V prípade akýchkoľvek otázok alebo problémov neváhajte kontaktovať',
            ) + ' '}
            <ProtectedEmailLink
              user={'portal-admin'}
              domain={'fri.uniza.sk'}
              text={t('správcu portálu')}
            />
            .
          </p>
        </div>
      </ContentBlock>
    </div>
  );
};

export default IpChangeRequestApprovalPage;
